import React, { useEffect } from "react"
import { useDispatch } from "react-redux"
import { Trans, useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import theme from "lib/material-ui/theme"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { sendOTPEmailSchema } from "validations/auth"
import { SERVER_ERROR } from "constants/errors"
import { updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { useForgetPasswordMutation } from "appRedux/slices/authApiSlice"
import CTA from "components/atoms/CTA"
import BottomSheet from "components/atoms/BottomSheet"
import TextField from "components/atoms/TextField"
import { updateEmail } from "appRedux/slices/resetPasswordSlice"
import { useAppSelector } from "appRedux/hooks"

type SendOTPBottomSheetProps = {
  show: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export default function SendOTPBottomSheetForLoggedOutUser(props: SendOTPBottomSheetProps) {
  const { show, onOpen, onClose } = props

  const enteredEmail = useAppSelector((state) => state.resetPassword.email)

  const [triggerForgetPassword, forgetPasswordResult] = useForgetPasswordMutation()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    register,
    getValues,
    setValue,
    reset,
    handleSubmit,
    formState: { errors, dirtyFields }
  } = useForm<SendOTPEmailRequestPayload>({
    resolver: zodResolver(sendOTPEmailSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: { email: "" }
  })

  useEffect(() => {
    if (enteredEmail) {
      setValue("email", enteredEmail, { shouldDirty: true })
    } else {
      reset({ email: "" })
    }
  }, [show])

  useEffect(() => {
    if (!Object.keys(errors).length) return
    handleErrors()
  }, [errors])

  function handleErrors() {
    if (errors.email) {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: SERVER_ERROR,
        show: true,
        status: 500,
        message: `auth.invalidEmail`
      }))
    }
  }

  function handleSendOTP(values: SendOTPEmailRequestPayload) {
    triggerForgetPassword(values.email)
    dispatch(updateEmail(values.email))
  }

  return (
    <BottomSheet
      id="send-otp-logged-out-user-bottom-sheet"
      title={t("auth.forgotPassword")}
      show={show}
      openBottomSheet={onOpen}
      closeBottomSheet={onClose}
    >
      <form onSubmit={handleSubmit(handleSendOTP)} style={{ width: "100%", padding: "0 12px" }}>
        <Stack marginTop={4} alignItems="center">
          <img
            src="/static/images/email.png"
            alt="landing image"
            style={{ width: "124px", height: "auto" }}
          />
          <Message>
            <Trans
              i18nKey={"auth.emailSentForPasswordRecovery"}
              components={{
                strong: <strong style={{
                  color: theme.palette.gray["15"],
                  fontWeight: 700
                }}/>
              }}
            />
          </Message>
          <TextField
            id="email"
            label={t("user.email")}
            type="email"
            defaultValue={getValues("email")}
            register={register}
            setValue={setValue}
            error={errors.email}
          />
          <CTA
            variant="contained"
            type="submit"
            disabled={Object.keys(dirtyFields).length !== 1 || forgetPasswordResult.isLoading}
            loading={forgetPasswordResult.isLoading}
            onClick={handleSubmit(handleSendOTP)}
          >
            {t("auth.sendOTP")}
          </CTA>
        </Stack>
      </form>
    </BottomSheet>
  )
}

const Message = styled(Typography)(({ theme }) => ({
  fontSize: "1.3rem",
  color: theme.palette.gray["14"],
  marginTop: "28px",
  marginBottom: "46px"
}))
